import { Model, View } from "@lernetz/ts-lib";
import { Pattern } from "../router/Route";
import { DefaultRouterContent, IRoute, Router } from "../router/Router";

import template from './Stack.html';

export class StackView extends View<Stack> {
    constructor( config?) {
        super( { template, ...config } );
    }
}


export class Stack extends Model {

    private stack: Segment[];

    readonly route: StackRoute;

    root: Segment;

    constructor( config: { pattern: string, root: Segment, name?: string; } ) {
        super( {} );
        this.route = new StackRoute( { pattern: config.pattern, name: config.name, stack: this } );

        this.stack = [];
        this.root = config.root;
    }


    async invoke( fullPath: string ) {

        // get all segments as array
        let params = this.route.params( fullPath );
        let urlSegments = params?.wild ? params.wild.split( '/' ) : [];


        // check every index of segments if url and stack match
        for( var index = 0; index < Math.min( this.stack.length, urlSegments.length ); index++ ) {
            if( this.stack[ index ].url != urlSegments[ index ] ) break;
        }

        // cut stack to matching index
        // start resolving for latest matching segment
        this.stack.splice( index );

        // cut url segments to start resolving from the sgements after the match
        urlSegments = urlSegments.slice( index );

        // load resuming segments of path
        for( const urlSeg of urlSegments ) {
            let s = await this.top.resolve( urlSeg );
            if( s ) this.stack.push( s );
        }

        // inform that the top has changed
        this.propertyChange.dispatch( { name:'top' } );
    }

    get top(): Segment {
        return this.stack.length == 0 ? this.root : this.stack[ this.stack.length - 1 ];
    }

    get segments() {
        return this.stack.slice();
    }

    /**
     * Pushes a new segment on top of the stack
     * @param s The new segment
     */
    push( s: Segment ) {
        this.set( [ ...this.stack, s ] );
    }

    /**
     * Removes the last / top segment on the stack
     */
    pop(): Segment {
        // do not pop root element
        if( this.stack.length == 0 ) return;

        this.set( this.stack.slice( 0, -1 ), true );
    }

    /**
     * Travels back in stack to the given segment
     * @param s the target segment
     */
    to( s: Segment ) {
        let index = this.stack.indexOf( s );
        if( index >= 0 ) {
            this.set( this.stack.splice( index ) );
        }
    }

    /**
     * Sets the stack with the given segments
     */
    set( segments: Segment | Segment[], restoreScroll = false ) {

        if( !Array.isArray( segments ) ) segments = [ segments ];

        this.route.router.toPath( this.route.segmentsPath( segments ), restoreScroll );
    }

}



export interface Segment {
    resolve( urlSegment: string ): Promise<Segment>;
    readonly url: string;
    readonly view: any,
    readonly data: any,
}


/**
 * A base class for segments that represents a model
 */
export class ModelSegment<T = any> implements Segment {


    constructor( public model: T ) {
    }

    resolve( childSegment: string ): Promise<Segment> {
        throw new Error( "Method not implemented." );
    }

    get view() {
        return this.model[ 'modelName' ];
    }

    get url() {
        return this.model[ 'id' ];
    }

    get data() {
        return this.model;
    }
}


/**
 * Inner class that handles route logic for the router.
 */
class StackRoute implements IRoute<DefaultRouterContent> {

    private pattern: Pattern;
    private content: { view: string, data: Stack; };

    router: Router;
    name: string;

    constructor( config: { pattern: string, name?: string, stack: Stack; } ) {
        this.pattern = new Pattern( config.pattern );
        this.content = { view: 'Stack', data: config.stack };
        this.name = config.name;
    }

    test( path: string ): boolean {
        return this.pattern.test( path );
    }
    async invoke( path: string, router: Router<DefaultRouterContent>, restoreScroll:boolean ) {

        this.router = router;
        router.content = this.content;

        await this.stack.invoke( path );

        // set scrollpos
        router.scrollHistory.setPos( path, restoreScroll );
        this.router.writeURL( this.pathOf() );
    }
    pathOf( params?: any,  ): string {
        return this.segmentsPath( this.stack.segments );
    }
    params( fullPath: string ) {
        return this.pattern.params( fullPath ) as { wild: string; };
    }

    segmentsPath( segments:Segment[] ) {
        return this.pattern.pathOf( { '*': segments.map( s => s.url ).join( '/' ) } );
    }

    get stack() {
        return this.content.data;
    }
}


